import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";

function isOpenStatus(status) {
  return ["nieuw", "in_behandeling", "actie_uitgezet"].includes(String(status || ""));
}

export default function KamDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError("");
        const res = await api.get("/kam/reports", { params: { limit: 200, sortBy: "createdAt", sortDir: "desc" } });
        setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
      } catch (err) {
        setError(err?.response?.data?.message || "KAM dashboard laden mislukt");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const stats = useMemo(() => {
    const reports = Array.isArray(items) ? items : [];
    const open = reports.filter((item) => isOpenStatus(item?.status)).length;
    const critical = reports.filter((item) => String(item?.severity || "") === "critical" && isOpenStatus(item?.status)).length;
    const actionsOpen = reports.reduce((sum, report) => {
      const count = (Array.isArray(report?.actions) ? report.actions : []).filter(
        (action) => !["done", "cancelled"].includes(String(action?.status || ""))
      ).length;
      return sum + count;
    }, 0);

    const byProject = new Map();
    const byClient = new Map();
    const byWorkorder = new Map();
    for (const report of reports) {
      const projectKey = String(report?.projectId?._id || report?.projectId || "").trim();
      const projectLabel = String(report?.projectId?.naam || report?.projectId?.title || projectKey || "-");
      const clientKey = String(report?.clientId?._id || report?.clientId || "").trim();
      const clientLabel = String(report?.clientId?.naam || clientKey || "-");
      const workorderKey = String(report?.workorderId?._id || report?.workorderId || "").trim();
      const workorderLabel = String(report?.workorderId?.werkbonNummer || workorderKey || "-");
      if (projectKey) byProject.set(projectLabel, (byProject.get(projectLabel) || 0) + 1);
      if (clientKey) byClient.set(clientLabel, (byClient.get(clientLabel) || 0) + 1);
      if (workorderKey) byWorkorder.set(workorderLabel, (byWorkorder.get(workorderLabel) || 0) + 1);
    }

    return {
      total: reports.length,
      open,
      critical,
      actionsOpen,
      topProjects: Array.from(byProject.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5),
      topClients: Array.from(byClient.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5),
      topWorkorders: Array.from(byWorkorder.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5),
    };
  }, [items]);

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">KAM cockpit</h2>
            <p className="text-sm text-gray-500">Open meldingen, kritieke signalen en opvolging.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/pl/kam/analyse" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              Analyse
            </Link>
            <Link to="/pl/kam/meldingen" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              Meldingen
            </Link>
            <Link to="/pl/kam/toolboxen" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              Toolboxen
            </Link>
          </div>
        </div>
      </section>

      {error ? <section className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</section> : null}

      <section className="grid gap-3 md:grid-cols-4">
        <StatCard label="Totaal meldingen" value={stats.total} loading={loading} />
        <StatCard label="Open meldingen" value={stats.open} loading={loading} />
        <StatCard label="Kritiek open" value={stats.critical} loading={loading} tone="danger" />
        <StatCard label="Open acties" value={stats.actionsOpen} loading={loading} />
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <TopList title="Per project" rows={stats.topProjects} loading={loading} />
        <TopList title="Per klant" rows={stats.topClients} loading={loading} />
        <TopList title="Per werkbon" rows={stats.topWorkorders} loading={loading} />
      </section>
    </div>
  );
}

function StatCard({ label, value, loading, tone = "default" }) {
  const toneClass =
    tone === "danger"
      ? "border-red-200 bg-red-50 text-red-800"
      : "border-gray-200 bg-white text-gray-900";
  return (
    <article className={`rounded-xl border p-4 shadow-sm ${toneClass}`}>
      <p className="text-xs uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{loading ? "..." : value}</p>
    </article>
  );
}

function TopList({ title, rows, loading }) {
  return (
    <section className="rounded-xl border bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {loading ? <p className="mt-2 text-sm text-gray-500">Laden...</p> : null}
      {!loading && rows.length === 0 ? <p className="mt-2 text-sm text-gray-500">Geen data.</p> : null}
      {!loading && rows.length > 0 ? (
        <ul className="mt-2 space-y-2 text-sm">
          {rows.map(([label, count]) => (
            <li key={`${label}-${count}`} className="flex items-center justify-between rounded border border-gray-100 px-2 py-1">
              <span className="truncate text-gray-700">{label}</span>
              <span className="font-semibold text-gray-900">{count}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
